import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { Permission } from '@prisma/client';

export const permissionMapping: Record<
  Permission,
  {
    title: string;
    description: string;
  }
> = {
  [Permission.FIGMA]: {
    title: 'Extension for Figma',
    description: 'Grants the ability to access and integrate with the Figma plugin.',
  },
  [Permission.PSD]: {
    title: 'Extension for Photoshop',
    description: 'Grants the ability to access and integrate with the Photoshop plugin.',
  },
  [Permission.WORD]: {
    title: 'Extension for Word',
    description: 'Grants the ability to access and integrate with the Microsoft Word plugin.',
  },
  [Permission.GOOGLE_DOC]: {
    title: 'Extension for Google Doc',
    description: 'Grants the ability to access and integrate with the Google Doc plugin.',
  },
  [Permission.VIEW_POLICY_ENGINE]: {
    title: 'View Policy Engine',
    description: 'Grants the ability to view the Policy Engine.',
  },
  [Permission.EDIT_POLICY_ENGINE]: {
    title: 'Edit Policy Engine',
    description: 'Grants the ability to edit the Policy Engine.',
  },
  [Permission.CONTENT_SCANNER]: {
    title: 'Content Scanner',
    description:
      'Grants access to the Content Scanner section, including creating web scraper jobs, managing, and analyzing scanned data.',
  },
  [Permission.INFLUENCER_MONITORING]: {
    title: 'Influencer Monitoring',
    description:
      'Grants access to the Influencer Monitoring section, including creating web scraper jobs from influencers on social media platforms (eg. Instagram, Tiktok, etc), managing, and analyzing scanned data.',
  },
  [Permission.REMEDIATION_QUEUE]: {
    title: 'Remediation Queue',
    description:
      'Grants access to the Remediation section, including monitoring and resolving issues with content, assets, and data from project management services like Jira, Asana, Wrike etc.',
  },
  [Permission.APPROVE_CLAIM]: {
    title: 'Approve Claim',
    description: 'Grants the authority to review and approve claims',
  },
  [Permission.APPROVE_TRADEMARK]: {
    title: 'Approve Trademark',
    description: 'Grants the authority to review and approve trade mark usages',
  },
  [Permission.ARCHIVE_QUEUE]: {
    title: 'Archive Queue',
    description: 'Grants the authority to review and archive documents',
  },
  [Permission.EDIT_SCANNER_JOB]: {
    title: 'Create, Edit, and Delete Scanner Jobs',
    description: 'Grants the authority to create, edit, and delete scanner jobs',
  },
  [Permission.EDIT_FILE_ACCESS]: {
    title: 'Create and Delete File System Access',
    description: 'Grants the authority to create and delete file system access',
  },
  [Permission.VIEW_MARKETING_TASK]: {
    title: 'View Marketing Task Boards',
    description: 'Grants the authority to view marketing task boards',
  },
  [Permission.EDIT_MARKETING_TASK]: {
    title: 'Create, Edit, and Delete Marketing Task Boards',
    description: 'Grants the authority to create, edit, and delete marketing task boards',
  },
  [Permission.VIEW_COMMUNICATION_REVIEW]: {
    title: 'View Communication Review',
    description: 'Grants the ability to view the Communication Review section',
  },
  [Permission.EDIT_COMMUNICATION_REVIEW_FILTER]: {
    title: 'Create, Edit, and Delete Email Filters',
    description: 'Grants the authority to create, edit, and delete email filters',
  },
  [Permission.EDIT_COMMUNICATION_REVIEW_INTEGRATION]: {
    title: 'Create, Edit, and Delete Communication Integrations',
    description: 'Grants the authority to create, edit, and delete communication integrations',
  },
  [Permission.VIEW_EMPLOYEE_REVIEW]: {
    title: 'View Outside Activities',
    description: 'Grants the ability to view outside activities and account information',
  },
  [Permission.APPROVE_OUTSIDE_ACCOUNTS]: {
    title: 'Approve Outside Accounts',
    description: 'Grants the authority to approve outside account registrations',
  },
  [Permission.REVIEW_OUTSIDE_TRANSACTIONS]: {
    title: 'Review Outside Transactions',
    description: 'Grants the authority to review and approve outside trading transactions',
  },
  [Permission.MANAGE_RESTRICTED_SECURITIES]: {
    title: 'Manage Restricted Securities',
    description: 'Grants the authority to manage the restricted securities list and alerts',
  },
  [Permission.EDIT_FEEDBACK_ANNOTATION]: {
    title: 'Edit, and Delete Feedback Annotations',
    description: 'Grants the authority to edit, and delete feedback annotations',
  },
  [Permission.APPROVE_FEEDBACK_ANNOTATION]: {
    title: 'Approve Feedback Annotations',
    description: 'Grants the authority to approve feedback annotations',
  },
  [Permission.ASSISTANT]: {
    title: 'Assistant Chatbot',
    description: 'Grants the authority to use the assistant chatbot',
  },
};

export default function defineAbilityFor(isAdmin: boolean = false, permissions: Permission[] = []) {
  const { can, build } = new AbilityBuilder(createMongoAbility);

  if (isAdmin) {
    can('manage', 'all'); // read-write access to everything
  } else {
    permissions.forEach((permission) => {
      can('do', permission);
    });
  }

  return build();
}
